import React, { useEffect, useMemo, useState } from 'react';
import { CalendarClock, Hash, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { SchedulePicker } from '@cotera/client/app/v1/trigger/schedule-picker';
import { useToolCallUpstreamSamples } from '../../../hooks/use-tool-call-upstream-samples';
import { asRecord, readString } from '../../utils';
import { Field } from './editor-field';
import {
  describeAccumulatedItem,
  resolveAccumulatedItemSample,
} from './bucket-accumulated-item';
import {
  buildUpstreamSources,
  singleSamplePointerSources,
} from './variable-picker-data';
import { UpstreamRefPickerButton } from './upstream-ref-picker';
import type { WorkflowNodeEditorProps } from './types';

const {
  BUCKET_REDUCER_OPS,
  isBucketReducerOp,
  isAddressableUpstreamFieldPath,
} = PersistedWorkflow;

/** Default schedule for a new time rule: daily at 9am. */
const DEFAULT_BUCKET_CRON = '0 9 * * *';

/**
 * The op names, capitalised rather than reworded. "How many different" is
 * clearer in the abstract and does not fit the control, and `distinct` is the
 * word anyone reaching for this already uses.
 */
const REDUCER_OP_LABELS: Record<PersistedWorkflow.BucketReducerOp, string> = {
  sum: 'Sum',
  avg: 'Average',
  min: 'Min',
  max: 'Max',
  first: 'First',
  last: 'Last',
  count: 'Count',
  distinct: 'Distinct',
};

/** A flush rule, as the form holds it. One shape for both kinds, so switching a
 * row's kind keeps whatever the other kind had typed into it. */
type TriggerRow = {
  readonly id: string;
  kind: 'count' | 'time';
  /** Count threshold, as typed. */
  threshold: string;
  /** Cron schedule (for a time rule). */
  cron: string;
  timezone: string;
};

type ReducerRow = {
  readonly id: string;
  op: PersistedWorkflow.BucketReducerOp;
  path: string;
  as: string;
};

function readTriggerRows(data: Record<string, unknown> | null): {
  combinator: 'or' | 'and';
  rows: TriggerRow[];
  nextId: number;
} {
  const policy = asRecord(data?.['policy']);
  const combinator = policy?.['combinator'] === 'and' ? 'and' : 'or';
  const rawTriggers = policy?.['triggers'];
  const rows: TriggerRow[] = [];
  let nextId = 1;
  if (Array.isArray(rawTriggers)) {
    for (const raw of rawTriggers) {
      const trigger = asRecord(raw);
      if (trigger === null) {
        continue;
      }
      if (trigger['kind'] === 'time') {
        rows.push({
          id: `trigger-${nextId}`,
          kind: 'time',
          threshold: '100',
          cron:
            typeof trigger['cron'] === 'string' && trigger['cron'] !== ''
              ? trigger['cron']
              : DEFAULT_BUCKET_CRON,
          timezone:
            typeof trigger['timezone'] === 'string' &&
            trigger['timezone'] !== ''
              ? trigger['timezone']
              : 'UTC',
        });
      } else {
        rows.push({
          id: `trigger-${nextId}`,
          kind: 'count',
          threshold: String(trigger['threshold'] ?? ''),
          cron: DEFAULT_BUCKET_CRON,
          timezone: 'UTC',
        });
      }
      nextId += 1;
    }
  }
  return { combinator, rows, nextId };
}

function readReducerRows(data: Record<string, unknown> | null): {
  rows: ReducerRow[];
  nextId: number;
} {
  const raw = data?.['reducers'];
  const rows: ReducerRow[] = [];
  let nextId = 1;
  if (Array.isArray(raw)) {
    for (const entry of raw) {
      const reducer = asRecord(entry);
      if (reducer === null) {
        continue;
      }
      const op = reducer['op'];
      rows.push({
        id: `reducer-${nextId}`,
        op: isBucketReducerOp(op) ? op : 'sum',
        path: typeof reducer['path'] === 'string' ? reducer['path'] : '',
        as: typeof reducer['as'] === 'string' ? reducer['as'] : '',
      });
      nextId += 1;
    }
  }
  return { rows, nextId };
}

function parsePositiveInt(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Reducer paths only. A reducer runs at flush time over items already stored,
 * where no ancestor outputs exist, so it cannot take the `Node/pointer` form
 * `accumulate` accepts — see `parseBucketConfig`.
 */
const isPointer = (value: string): boolean =>
  value === '' || value.startsWith('/');

/** Why what is typed in a field is not usable, said next to that field. */
const FieldWarning: React.FC<{ message: string }> = ({ message }) => (
  <div
    className="flex items-start gap-1.5 text-sm text-yellow-800"
    role="alert"
  >
    <TriangleAlert className="mt-1 size-3.5 shrink-0" />
    <span className="min-w-0">{message}</span>
  </div>
);

/**
 * A rule row: its own card, so the delete button belongs to one rule.
 *
 * The header is one line, and stays one line: it is `items-center`, so anything
 * that wraps there leaves the icon centred on a two-line box and visibly below
 * the control it labels. Keep header controls content-sized.
 */
const RuleCard: React.FC<{
  icon: React.ReactNode;
  onRemove: () => void;
  removeLabel: string;
  header: React.ReactNode;
  children?: React.ReactNode;
}> = ({ icon, onRemove, removeLabel, header, children }) => (
  <div className="border-input flex flex-col gap-2 rounded-md border p-3">
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground shrink-0">{icon}</span>
      {header}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="ml-auto shrink-0"
        title={removeLabel}
        aria-label={removeLabel}
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
    {children}
  </div>
);

/**
 * Config editor for a bucket node: when to flush, what to keep from each run,
 * and what to work out across the batch.
 *
 * Reports `null` until at least one rule is valid.
 */
export const BucketNodeEditor: React.FC<WorkflowNodeEditorProps> = ({
  node,
  onChange,
}) => {
  const data = asRecord(node.data);
  const seededTriggers = readTriggerRows(data);
  const seededReducers = readReducerRows(data);
  const [combinator, setCombinator] = useState<'or' | 'and'>(
    seededTriggers.combinator
  );
  const [triggers, setTriggers] = useState<TriggerRow[]>(seededTriggers.rows);
  const [nextTriggerId, setNextTriggerId] = useState(seededTriggers.nextId);
  const [accumulate, setAccumulate] = useState<string>(
    readString(data, 'accumulate') ?? ''
  );
  const [reducers, setReducers] = useState<ReducerRow[]>(seededReducers.rows);
  const [nextReducerId, setNextReducerId] = useState(seededReducers.nextId);

  // Re-seed local state when the drawer is pointed at a different node (the
  // drawer is one reused instance).
  const [seededNodeId, setSeededNodeId] = useState(node.id);
  if (seededNodeId !== node.id) {
    setSeededNodeId(node.id);
    const next = asRecord(node.data);
    const nextTriggers = readTriggerRows(next);
    const nextReducers = readReducerRows(next);
    setCombinator(nextTriggers.combinator);
    setTriggers(nextTriggers.rows);
    setNextTriggerId(nextTriggers.nextId);
    setAccumulate(readString(next, 'accumulate') ?? '');
    setReducers(nextReducers.rows);
    setNextReducerId(nextReducers.nextId);
  }

  const { upstreamSamples } = useToolCallUpstreamSamples(node.id);
  const directSample = useMemo(
    () =>
      upstreamSamples.find((sample) => sample.sourceDepth === 1) ??
      upstreamSamples[0],
    [upstreamSamples]
  );

  // Every upstream step, not just the one before, because `accumulate` now
  // takes a `Node/pointer` reference. The hook already emits node-scoped refs,
  // so this is the same call the debounce editor makes.
  const accumulateSources = useMemo(
    () => buildUpstreamSources(upstreamSamples),
    [upstreamSamples]
  );

  // Reducers read each accumulated item with a bare pointer, so their picker
  // offers the fields of one item — followed through whatever `accumulate`
  // selected, which may now be a slice of an ancestor rather than of this
  // step's own input.
  const reducerSources = useMemo(
    () =>
      singleSamplePointerSources({
        sample: resolveAccumulatedItemSample({
          accumulate,
          directSample,
          upstreamSamples,
        }),
        sourceName: 'item',
        label: 'Each item',
      }),
    [directSample, upstreamSamples, accumulate]
  );

  const sourceLabels = useMemo(
    () =>
      new Map(
        upstreamSamples.map(
          (sample) => [sample.sourceName, sample.label] as const
        )
      ),
    [upstreamSamples]
  );
  // Names the slice these fields came from, so the reducer picker is plainly
  // the consequence of the accumulate path above it rather than a second,
  // unexplained list.
  const itemHeading = useMemo(
    () => describeAccumulatedItem({ accumulate, sourceLabels }),
    [accumulate, sourceLabels]
  );

  useEffect(() => {
    const validTriggers = triggers.flatMap(
      (row): PersistedWorkflow.BucketFlushTrigger[] => {
        if (row.kind === 'count') {
          const threshold = parsePositiveInt(row.threshold);
          return threshold === undefined ? [] : [{ kind: 'count', threshold }];
        }
        return row.cron.trim() === ''
          ? []
          : [{ kind: 'time', cron: row.cron, timezone: row.timezone }];
      }
    );
    if (validTriggers.length === 0) {
      onChange(null);
      return;
    }
    const validReducers = reducers.flatMap((row) => {
      const as = row.as.trim();
      const path = row.path.trim();
      if (as === '' || !isPointer(path)) {
        return [];
      }
      return [{ op: row.op, path, as }];
    });
    const accumulatePath = accumulate.trim();
    if (!isAddressableUpstreamFieldPath(accumulatePath)) {
      onChange(null);
      return;
    }
    onChange({
      type: 'bucket',
      data: {
        policy: { combinator, triggers: validTriggers },
        ...(accumulatePath !== '' ? { accumulate: accumulatePath } : {}),
        ...(validReducers.length > 0 ? { reducers: validReducers } : {}),
      },
    });
  }, [combinator, triggers, accumulate, reducers, onChange]);

  const updateTrigger = (id: string, patch: Partial<TriggerRow>): void => {
    setTriggers((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  };
  const removeTrigger = (id: string): void => {
    setTriggers((prev) => prev.filter((row) => row.id !== id));
  };
  const addTrigger = (kind: 'count' | 'time'): void => {
    const id = `trigger-${nextTriggerId}`;
    setNextTriggerId((n) => n + 1);
    setTriggers((prev) => [
      ...prev,
      {
        id,
        kind,
        threshold: '100',
        cron: DEFAULT_BUCKET_CRON,
        timezone: 'UTC',
      },
    ]);
  };

  const updateReducer = (id: string, patch: Partial<ReducerRow>): void => {
    setReducers((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  };
  const removeReducer = (id: string): void => {
    setReducers((prev) => prev.filter((row) => row.id !== id));
  };
  const addReducer = (): void => {
    const id = `reducer-${nextReducerId}`;
    setNextReducerId((n) => n + 1);
    setReducers((prev) => [...prev, { id, op: 'sum', path: '', as: '' }]);
  };

  const accumulateInvalid = !isAddressableUpstreamFieldPath(accumulate.trim());

  return (
    <div className="flex flex-col gap-5">
      <Field
        label="Flush when"
        hint="Output is held here until a rule fires, then the branch below runs once with everything claimed."
      >
        {/* Above the rules, not under them: it governs how they combine, so
            the section reads top-down as "any of these: …" rather than making
            you reach the end of the list before learning how to read it. It
            still appears exactly when it always has — more than one rule. */}
        {triggers.length > 1 ? (
          <Select
            value={combinator}
            onValueChange={(next) =>
              setCombinator(next === 'and' ? 'and' : 'or')
            }
          >
            <SelectTrigger className="h-9 w-full" aria-label="Combine triggers">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="or">
                Any trigger (whichever comes first)
              </SelectItem>
              <SelectItem value="and">All triggers</SelectItem>
            </SelectContent>
          </Select>
        ) : null}

        <div className="flex flex-col gap-3">
          {triggers.length === 0 ? (
            <div className="border-input text-muted-foreground rounded-md border border-dashed p-3 text-sm">
              No rule yet, so nothing will ever flush.
            </div>
          ) : null}

          {triggers.map((row) => (
            <RuleCard
              key={row.id}
              icon={
                row.kind === 'count' ? (
                  <Hash className="h-4 w-4" />
                ) : (
                  <CalendarClock className="h-4 w-4" />
                )
              }
              onRemove={() => removeTrigger(row.id)}
              removeLabel="Remove rule"
              header={
                <div className="flex min-w-0 items-center gap-2">
                  <Select
                    value={row.kind}
                    onValueChange={(next) =>
                      updateTrigger(row.id, {
                        kind: next === 'time' ? 'time' : 'count',
                      })
                    }
                  >
                    {/* No width and no height here, both on purpose.
 
                        Width: SelectTrigger is `w-fit` by default, which is how
                        the native select this replaced behaved — about 100px for
                        "Item count". Pinning it wide enough for "On a schedule"
                        instead cost every count row ~75px it did not have, and
                        that, not the row's contents, is what pushed "items" onto
                        a second line. Content-sized, the longest label fits
                        without clipping and the short one costs nothing.
 
                        Height: SelectTrigger sets its own via
                        `data-[size=default]:h-9`, and an attribute selector
                        outranks a plain `h-8` utility — so writing one looks
                        applied, does nothing, and leaves the select 4px taller
                        than the input beside it. Height comes from `size`. */}
                    <SelectTrigger aria-label="Rule kind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="count">Item count</SelectItem>
                      <SelectItem value="time">On a schedule</SelectItem>
                    </SelectContent>
                  </Select>
                  {row.kind === 'count' ? (
                    <>
                      <Input
                        type="number"
                        min={1}
                        aria-label="Item threshold"
                        value={row.threshold}
                        placeholder="100"
                        className="w-20"
                        onChange={(event) =>
                          updateTrigger(row.id, {
                            threshold: event.target.value,
                          })
                        }
                      />
                      <Text.Caption className="text-foreground shrink-0">
                        items
                      </Text.Caption>
                    </>
                  ) : null}
                </div>
              }
            >
              {row.kind === 'time' ? (
                <SchedulePicker
                  value={row.cron}
                  onChange={(cron) => updateTrigger(row.id, { cron })}
                />
              ) : null}
            </RuleCard>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-9 w-full justify-center gap-2"
            onClick={() => addTrigger('count')}
          >
            <Plus className="h-4 w-4" />
            Count rule
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-9 w-full justify-center gap-2"
            onClick={() => addTrigger('time')}
          >
            <Plus className="h-4 w-4" />
            Schedule
          </Button>
        </div>
      </Field>

      <Field
        label="Keep from each run"
        hint="A field to keep from each run, from this step or an earlier one. Blank keeps the whole value."
      >
        <div className="flex items-center gap-2">
          <Input
            value={accumulate}
            placeholder="/order"
            aria-label="Accumulate pointer"
            aria-invalid={accumulateInvalid}
            className="flex-1"
            onChange={(event) => setAccumulate(event.target.value)}
          />
          <UpstreamRefPickerButton
            sources={accumulateSources}
            onPick={(ref) => setAccumulate(ref)}
            title="Use a field from an earlier step"
          />
        </div>
        {accumulateInvalid ? (
          <FieldWarning message="Use an earlier step's field, or a pointer like /order. Blank keeps the whole value." />
        ) : null}
      </Field>

      <Field
        label="Work out across the batch"
        hint="Optional. Named totals over each flush's items, read from whatever is kept above."
      >
        <div className="flex flex-col gap-2">
          {reducers.map((row) => {
            const pathInvalid = !isPointer(row.path.trim());
            return (
              <div key={row.id} className="flex flex-col gap-1">
                <div className="grid grid-cols-[9rem_1fr_7rem_2.25rem] items-center gap-2">
                  <Select
                    value={row.op}
                    onValueChange={(next) => {
                      if (isBucketReducerOp(next)) {
                        updateReducer(row.id, { op: next });
                      }
                    }}
                  >
                    <SelectTrigger className="w-full" aria-label="Aggregate">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BUCKET_REDUCER_OPS.map((op) => (
                        <SelectItem key={op} value={op}>
                          {REDUCER_OP_LABELS[op]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex min-w-0 items-center gap-1">
                    <Input
                      value={row.path}
                      placeholder="/total"
                      aria-label="Field in each item"
                      aria-invalid={pathInvalid}
                      className="min-w-0 flex-1"
                      onChange={(event) =>
                        updateReducer(row.id, { path: event.target.value })
                      }
                    />
                    <UpstreamRefPickerButton
                      sources={reducerSources}
                      onPick={(ref) => updateReducer(row.id, { path: ref })}
                      title="Use a field from each item"
                      heading={itemHeading}
                    />
                  </div>
                  <Input
                    value={row.as}
                    placeholder="total"
                    aria-label="Output name"
                    className="w-full"
                    onChange={(event) =>
                      updateReducer(row.id, { as: event.target.value })
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    title="Remove aggregate"
                    aria-label="Remove aggregate"
                    onClick={() => removeReducer(row.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {pathInvalid ? (
                  <FieldWarning message="Use a pointer like /total. Blank uses the whole item." />
                ) : null}
              </div>
            );
          })}
        </div>
        <Button
          type="button"
          variant="outline"
          className="h-9 justify-start gap-2"
          onClick={addReducer}
        >
          <Plus className="h-4 w-4" />
          Add reducer
        </Button>
      </Field>
    </div>
  );
};
